/**
 * [MCV-1524] Start-date floor for listing wizards.
 *
 * A listing could be created with a start date in the past — nothing rejected
 * it. The guard cannot simply be "must be today or later", though: a listing
 * created last month legitimately has a start date last month, and its owner
 * still needs to edit the rest of the wizard. Rejecting the value already on
 * the record would trap them on this step with no way forward.
 *
 * So the rule is: a start date may not be moved into the past. A past date that
 * is already persisted stays acceptable for as long as it is left alone.
 */

/** Today as `YYYY-MM-DD` in the viewer's local timezone. */
export const todayIsoDate = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * `true` when `startDate` is acceptable.
 *
 * Compared as `YYYY-MM-DD` strings, which sort lexicographically in date order
 * and sidesteps the timezone drift that `new Date('2026-07-30')` (parsed as
 * UTC midnight) introduces for anyone west of Greenwich.
 */
export const isStartDateAllowed = (startDate: string, persistedStartDate?: string): boolean => {
  if (!startDate) return true; // absence is the required-field check's business
  if (persistedStartDate && startDate === persistedStartDate) return true;
  return startDate >= todayIsoDate();
};

/**
 * The earliest date the picker should offer.
 *
 * Today, unless the record already carries an earlier date — that one has to
 * stay selectable so an in-flight listing can still be edited.
 *
 * A *later* persisted date must not raise the floor. `isStartDateAllowed`
 * accepts anything from today onward, so flooring at a future start date would
 * make the picker stricter than the schema and strand anyone trying to pull a
 * future listing earlier. Keep this in step with `isStartDateAllowed`.
 */
export const startDateFloor = (persistedStartDate?: string): string => {
  const today = todayIsoDate();
  return persistedStartDate && persistedStartDate < today ? persistedStartDate : today;
};

export const START_DATE_IN_PAST_MESSAGE = 'Start date cannot be in the past';
